<script setup lang="ts">
import { useDateFormat } from '@ja/core-ui/lib/date'

import { useCommunicationTranslations } from '../composables/useCommunicationTranslations'
import { type UnifiedMessage } from '../composables/useConversations'
import { useMessenger } from '../composables/useMessenger'
import type { CommunicationPriority } from '../types/communication'

import AnnouncementLink from './AnnouncementDisplay/AnnouncementLink.vue'

defineProps<{
  conversations: UnifiedMessage[]
  isLoading: boolean
}>()

const messenger = useMessenger()
const { tm } = useCommunicationTranslations('communication')
const { formatRelative } = useDateFormat()
const formatTime = (dateString: string) => formatRelative(dateString)

const emit = defineEmits<{
  'view-memo': [memo: UnifiedMessage & { unifiedType: 'inbox' }]
  selected: []
}>()

const handleItemClick = (item: UnifiedMessage) => {
  if (item.unifiedType === 'conversation') {
    messenger.openChat(
      item.id,
      item.communication_id,
      item.title,
      item.subtitle
    )
  } else {
    emit('view-memo', item)
  }
  emit('selected')
}
</script>

<template>
  <div class="max-h-[400px] overflow-y-auto">
    <div v-if="isLoading" class="p-4 text-center text-sm text-muted-foreground">
      {{ tm('messages.loading_conversations') }}
    </div>
    <div v-else-if="conversations.length === 0" class="p-4 text-center text-sm text-muted-foreground">
      {{ tm('messages.no_conversations_found') }}
    </div>
    <div v-else>
      <template v-for="item in conversations" :key="item.id">
        <!-- Inbox items (announcements) use AnnouncementLink -->
        <AnnouncementLink
          v-if="item.unifiedType === 'inbox'"
          :title="item.title"
          :priority="(item.priority as CommunicationPriority) || 'normal'"
          :type="item.type"
          :is-read="item.is_read"
          :date="item.timestamp"
          @click="handleItemClick(item)"
        />

        <!-- Conversations keep existing rendering -->
        <div
          v-else
          class="flex cursor-pointer items-start gap-3 border-b p-3 transition-colors hover:bg-muted last:border-0"
          @click="handleItemClick(item)"
        >
          <div class="bg-primary/10 relative flex size-10 shrink-0 items-center justify-center rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" class="text-primary size-5" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clip-rule="evenodd" />
            </svg>
          </div>
          <div class="min-w-0 flex-1">
            <div class="flex items-center justify-between gap-2">
              <p class="truncate text-sm font-medium">
                {{ item.title }}
              </p>
              <span class="shrink-0 text-[10px] text-muted-foreground">
                {{ formatTime(item.timestamp) }}
              </span>
            </div>
            <p class="mt-0.5 line-clamp-1 text-xs text-muted-foreground">
              <span v-if="item.latest_message?.sender" class="font-medium text-foreground/80">
                {{ item.latest_message.sender.name }}:
              </span>
              {{ item.latest_message?.content || tm('messages.no_messages_yet') }}
            </p>
            <p v-if="item.type === 'communication' && item.subtitle" class="mt-1 truncate text-[10px] text-muted-foreground/60">
              {{ tm('messages.created_by', { owner: item.subtitle }) }}
            </p>
            <p v-else-if="item.type === 'direct' && 'participant_names' in item && item.participant_names" class="mt-1 truncate text-[10px] text-muted-foreground/60">
              {{ tm('messages.with') }} {{ item.participant_names }}
            </p>
          </div>
        </div>
      </template>
    </div>
  </div>
</template>
